import { Button, Empty, Tooltip } from "antd";
import { createContext, ReactNode, useContext } from "react";

export type BlankStateAction = {
  label: ReactNode;
  onClick: () => void;
  icon?: ReactNode;
  /** Figma places some icons after the label (e.g. "Import CSV ⇤", "Add New +"). */
  iconPlacement?: "start" | "end";
  disabled?: boolean;
  /** Tooltip shown while disabled (e.g. permission message). */
  disabledTitle?: string;
  /**
   * Button style. Defaults to "primary" for `primaryAction` and "default" for
   * `secondaryActions`; set it when the design highlights an action that is
   * not first (e.g. Design Scope: Add New · **Import CSV** · Import List).
   */
  type?: "primary" | "default";
  "data-testid"?: string;
};

export type BlankStateProps = {
  title: ReactNode;
  description?: ReactNode;
  /** Defaults to the standard "No Data" illustration. */
  icon?: ReactNode;
  /** Rendered first. */
  primaryAction?: BlankStateAction;
  /** Rendered after `primaryAction`, in the given order. */
  secondaryActions?: BlankStateAction[];
  className?: string;
  "data-testid"?: string;
};

function BlankStateButton({
  action,
  primary
}: {
  action: BlankStateAction;
  primary: boolean;
}) {
  const {
    label,
    onClick,
    icon,
    iconPlacement = "start",
    disabled = false,
    disabledTitle,
    type = primary ? "primary" : "default",
    "data-testid": dataTestId
  } = action;
  return (
    <Tooltip title={disabled ? disabledTitle : undefined}>
      <span className="inline-flex">
        <Button
          type={type}
          icon={icon}
          iconPlacement={iconPlacement}
          disabled={disabled}
          onClick={onClick}
          data-testid={dataTestId}
        >
          {label}
        </Button>
      </span>
    </Tooltip>
  );
}

/**
 * Shared empty state for log pages: illustration, title, helper text and the
 * create/import CTAs. Callers pass the same handlers their toolbar uses.
 */
export default function BlankState({
  title,
  description,
  icon,
  primaryAction,
  secondaryActions = [],
  className = "",
  "data-testid": dataTestId = "blank-state"
}: BlankStateProps) {
  const hasActions = Boolean(primaryAction) || secondaryActions.length > 0;
  return (
    <div
      className={`pointer-events-auto flex flex-col items-center justify-center px-4 text-center ${className}`}
      data-testid={dataTestId}
    >
      {icon ?? <Empty className="m-0" description="No Data" />}
      <div className="mt-4 text-base font-semibold text-ciq-fg">{title}</div>
      {description ? (
        <div className="mt-1 max-w-sm text-sm text-ciq-fg-subtle">
          {description}
        </div>
      ) : null}
      {hasActions ? (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
          {primaryAction ? (
            <BlankStateButton action={primaryAction} primary />
          ) : null}
          {secondaryActions.map((action, index) => (
            <BlankStateButton
              // Actions are a short static list; order never changes.
              key={index}
              action={action}
              primary={false}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * AG Grid renders overlay components in a portal, so React context reaches
 * them. Pages wrap the grid in this provider with whatever should show when
 * there are no rows (blank state vs. "no results"), and pass
 * `GridNoRowsOverlay` as `noRowsOverlayComponent`.
 */
export const GridNoRowsOverlayContext = createContext<ReactNode>(null);

export function GridNoRowsOverlay() {
  return <>{useContext(GridNoRowsOverlayContext)}</>;
}
